import React, { useEffect, useMemo, useRef } from 'react';
import { lyricsBlocks } from '../../../../services/songSectionsModel';
import { activeLineIndex, lineTimes, seekTargetFor } from '../songpanel/lyricLineTimes';
import './LyricsWithSections.css';

// After a teacher scrolls the lyrics by hand, leave them where she put them
// for this long before following the song again.
const HAND_SCROLL_MS = 4000;

const scrollParentOf = (el) => {
	for (let p = el && el.parentElement; p; p = p.parentElement) {
		const oy = window.getComputedStyle ? window.getComputedStyle(p).overflowY : '';
		if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
	}
	return null;
};

/**
 * Lyrics with their section labels (2026-09-25). Each label is a button: tap
 * "Chorus 2" while the student sings it and the next jot lands there, the same
 * as tapping the chip. With no sections this renders the plain text exactly as
 * the two lyric surfaces always did (`className` is theirs).
 *
 * FOLLOWING THE TRACK (2026-09-30, songpanel/lyricLineTimes.js). With
 * `follow` ({ timed, currentTime, playing, onSeek }) — a track of THIS song
 * is open and has timed words — each line renders on its own: the line
 * being sung is highlighted, a line whose words were found in the track is a
 * button that plays from just before it (Apple Music's tap), and the pane
 * keeps the sung line in view while playing unless the teacher has just
 * scrolled it herself. Without `follow`, nothing here changes.
 */
const LyricsWithSections = ({ text, sections = null, activeId = null, onSelect = null, className, follow = null }) => {
	const blocks = lyricsBlocks(text, sections);
	const hasSections = blocks.some((b) => b.section);
	const timed = follow && Array.isArray(follow.timed) && follow.timed.length ? follow.timed : null;

	// Every rendered line in order, so one index runs across the blocks.
	const flat = useMemo(() => blocks.flatMap((b) => b.lines), [text, sections]); // eslint-disable-line react-hooks/exhaustive-deps
	const times = useMemo(() => (timed ? lineTimes(flat, timed) : null), [flat, timed]);
	// currentTime null: no track of this song is open, so nothing is being sung
	// (a tap starts one).
	const active = times && follow.currentTime !== null && follow.currentTime !== undefined ? activeLineIndex(times, Number(follow.currentTime) || 0) : -1;
	const playing = Boolean(follow && follow.playing);

	const activeRef = useRef(null);
	const handScrollAt = useRef(0);
	useEffect(() => {
		const el = activeRef.current;
		if (!playing || !el || Date.now() - handScrollAt.current < HAND_SCROLL_MS) return;
		const box = scrollParentOf(el);
		if (!box || typeof box.scrollTo !== 'function') return;
		const r = el.getBoundingClientRect();
		const b = box.getBoundingClientRect();
		// Keep the sung line in the upper third, Apple-style; only move when it
		// has left the comfortable middle band, so the pane is not always moving.
		if (r.top >= b.top + b.height * 0.15 && r.bottom <= b.top + b.height * 0.7) return;
		box.scrollTo({ top: box.scrollTop + (r.top - b.top) - b.height * 0.3, behavior: 'smooth' });
	}, [active, playing]);

	if (!hasSections && !times) return <p className={className}>{text}</p>;

	const markHand = () => {
		handScrollAt.current = Date.now();
	};

	let n = 0;
	const renderLines = (lines) => {
		if (!times) return <p className={className}>{lines.join('\n')}</p>;
		return (
			<div className={`${className || ''} ld-lyriclines`}>
				{lines.map((line) => {
					const i = n++;
					const t = times[i];
					const isActive = i === active;
					const cls = `ld-lyricline${isActive ? ' is-active' : ''}`;
					if (t === null) {
						return (
							<div key={i} className={cls}>
								{line.trim() ? line : ' '}
							</div>
						);
					}
					return (
						<button
							key={i}
							type="button"
							ref={isActive ? activeRef : null}
							className={`${cls} ld-lyricline--tap`}
							aria-current={isActive ? 'true' : undefined}
							title="Play from this line"
							onClick={() => follow.onSeek && follow.onSeek(seekTargetFor(t))}>
							{line}
						</button>
					);
				})}
			</div>
		);
	};

	return (
		<div className="ld-lyricsecs" onWheel={markHand} onTouchMove={markHand}>
			{times && <p className="ld-lyricfollow-hint">Tap a line to play from there.</p>}
			{blocks.map((b, i) => (
				<div key={b.section ? b.section.id : `pre-${i}`} className="ld-lyricsec">
					{b.section && (
						<button
							type="button"
							className={`ld-lyricsec__label${activeId === b.section.id ? ' is-active' : ''}`}
							aria-pressed={activeId === b.section.id}
							disabled={!onSelect}
							onClick={() => onSelect && onSelect(activeId === b.section.id ? null : b.section.id)}>
							{/* Pass 3 D4: the same check the section chips carry. */}
							{activeId === b.section.id && <i className="fa-sharp fa-solid fa-check ld-secchip__check" aria-hidden="true" />}
							{b.section.label}
						</button>
					)}
					{b.lines.length > 0 && renderLines(b.lines)}
				</div>
			))}
		</div>
	);
};

export default LyricsWithSections;
